import { api } from './api-client';
import { STORAGE_KEYS, getItem } from './storage';
import type { NoticeCategory, NoticeItem } from '@/hooks/useNotices';

interface BackendAnnouncement {
  id: string;
  title: string;
  content: string;
  type?: string;
  priority?: string;
  scope?: string;
  publisherId?: string;
  publisherName?: string;
  publishAt?: string | null;
  expireAt?: string | null;
  isPinned?: boolean;
  attachments?: Array<{ name?: string }> | null;
  status?: string;
  viewCount?: number;
  createdAt: string;
}

function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

/**
 * Backend has `type` (NOTICE/NEWS/POLICY/EVENT) + `priority` (LOW/NORMAL/HIGH/URGENT).
 * Mobile has 4 categories (important/general/hr/safety). Map deliberately —
 * URGENT priority always wins "important"; POLICY routes to hr (HR policies
 * live there); EVENT+NEWS share general; type POLICY with safety keyword → safety.
 */
function mapCategory(a: BackendAnnouncement): Exclude<NoticeCategory, 'all'> {
  if (a.priority === 'URGENT') return 'important';
  const title = (a.title ?? '').toLowerCase();
  if (/safety|安全|事故|incident/i.test(title)) return 'safety';
  if (a.type === 'POLICY') return 'hr';
  return 'general';
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const ms = now.getTime() - d.getTime();
  if (ms < 48 * 3_600_000) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function adapt(a: BackendAnnouncement): NoticeItem {
  const date = a.publishAt ?? a.createdAt;
  return {
    id: a.id,
    category: mapCategory(a),
    title: a.title,
    body: a.content,
    date: formatShortDate(date),
    authorName: a.publisherName ?? '',
    authorInitials: initials(a.publisherName),
    attachmentCount: Array.isArray(a.attachments) ? a.attachments.length : 0,
    unread: true,
  };
}

export async function fetchAnnouncements(): Promise<NoticeItem[]> {
  const { data } = await api.get<BackendAnnouncement[]>('/oa/announcement', {
    params: { status: 'PUBLISHED' },
  });
  return data.map(adapt);
}

export async function markAnnouncementRead(id: string): Promise<void> {
  const userRaw = await getItem(STORAGE_KEYS.USER);
  if (!userRaw) return;
  const userId = (JSON.parse(userRaw) as { id: string }).id;
  await api.post(`/oa/announcement/${id}/read`, { userId });
}
